import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, LogOut, Star } from "lucide-react";
import { toast } from "sonner";
import PaywallModal from "@/components/PaywallModal";

const DEFAULT_DATA = {
  personal: { name: "Your Name", title: "Your Title", email: "you@mail.com", phone: "", location: "", website: "" },
  summary: "",
  experience: [],
  education: [],
  skills: [],
  certificates: [],
  photo: null,
};
const DEFAULT_STYLE = {
  fontFamily: "font-plex-r",
  fontSize: 10,
  accent: "#0047AB",
  paper: "A4",
  spacing: "cozy",
};

export default function Dashboard() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/resumes");
      setResumes(data);
    } catch (e) {
      toast.error("Failed to load resumes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createNew = async () => {
    const { data } = await api.post("/resumes", {
      title: "Untitled Resume",
      template: "brutalist",
      data: DEFAULT_DATA,
      style: DEFAULT_STYLE,
    });
    navigate(`/editor/${data.resume_id}`);
  };

  const removeResume = async (id) => {
    if (!confirm("Delete this resume?")) return;
    await api.delete(`/resumes/${id}`);
    toast.success("Resume deleted");
    load();
  };

  return (
    <div className="min-h-screen">
      <nav className="bg-white sticky top-0 z-30" style={{borderBottom:"1px solid #0A0A0A"}}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl font-black">RESUME</Link>
          <div className="flex items-center gap-4">
            {user?.is_pro && <span className="bg-[#FFD700] text-black label-eyebrow px-2 py-1">PRO</span>}
            <div className="hidden sm:flex items-center gap-2">
              {user?.picture && <img src={user.picture} className="w-8 h-8 brut-border" alt="avatar" />}
              <span className="text-sm">{user?.name}</span>
            </div>
            <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="brut-border px-3 py-2 text-sm flex items-center gap-1"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="label-eyebrow">Workspace</div>
            <h1 className="font-display text-5xl md:text-6xl uppercase font-black leading-none mt-2">Your resumes.</h1>
          </div>
          <div className="flex gap-3">
            {!user?.is_pro && (
              <button data-testid="upgrade-btn" onClick={() => setShowPaywall(true)} className="brut-border brut-shadow bg-[#FFD700] px-5 py-3 font-medium flex items-center gap-2"><Star className="w-4 h-4" /> Unlock Pro</button>
            )}
            <button data-testid="new-resume-btn" onClick={createNew} className="brut-border brut-shadow bg-black text-white px-5 py-3 font-medium flex items-center gap-2"><Plus className="w-4 h-4" /> New resume</button>
          </div>
        </div>

        {loading ? (
          <div className="text-neutral-600">Loading…</div>
        ) : resumes.length === 0 ? (
          <div className="bg-white brut-border p-16 text-center brut-shadow" data-testid="empty-state">
            <div className="font-display text-3xl font-black mb-3">No resumes yet.</div>
            <p className="text-neutral-600 mb-8">Start with a blank canvas — we'll help you fill it in with AI.</p>
            <button data-testid="empty-new-btn" onClick={createNew} className="brut-border brut-shadow bg-black text-white px-6 py-3 font-medium">Draft your first resume →</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resumes.map((r) => (
              <div key={r.resume_id} className="bg-white brut-border brut-shadow p-6 flex flex-col" data-testid={`resume-card-${r.resume_id}`}>
                <div className="aspect-[3/4] bg-[#F4F4F0] brut-border p-4 mb-4 flex flex-col">
                  <div className="label-eyebrow text-neutral-500">{r.template}</div>
                  <div className="font-display text-xl font-black mt-2 leading-tight truncate">{r.data?.personal?.name || "Untitled"}</div>
                  <div className="text-xs text-neutral-600 truncate">{r.data?.personal?.title || ""}</div>
                  <div className="mt-3 h-[1px] bg-black" />
                  <div className="mt-3 space-y-1.5 flex-1">
                    {Array.from({length: 8}).map((_,i)=>(
                      <div key={i} className="h-1 bg-neutral-300" style={{width:`${50 + ((i*17)%45)}%`}} />
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display text-lg font-bold truncate">{r.title}</div>
                    <div className="text-xs text-neutral-500">Updated {new Date(r.updated_at).toLocaleDateString()}</div>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`open-${r.resume_id}`} onClick={() => navigate(`/editor/${r.resume_id}`)} className="brut-border px-3 py-1.5 text-sm">Edit</button>
                    <button data-testid={`delete-${r.resume_id}`} onClick={() => removeResume(r.resume_id)} className="brut-border px-2 py-1.5 text-sm"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {showPaywall && (
        <PaywallModal onClose={() => setShowPaywall(false)} onSuccess={async () => { await checkAuth(); setShowPaywall(false); toast.success("Welcome to Pro!"); }} />
      )}
    </div>
  );
}
